// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { classNames } from '@shared/ui-kit/utils';
import { createColumnHelper, type ExpandedState } from '@tanstack/react-table';
import { Table } from '@shared/ui-kit/components';
import { type FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Translation } from '@shared/ui-kit/providers';

export interface CashflowTableRow {
  readonly id: string;
  readonly concept: string;
  readonly currency: string;
  readonly type: 'inflows' | 'outflows' | 'inflow-detail' | 'outflow-detail';
  readonly month?: string;
  readonly subRows?: CashflowTableRow[];
  readonly [key: string]: string | number | readonly CashflowTableRow[] | undefined;
}

export interface CashflowTableProps {
  readonly data: CashflowTableRow[];
  readonly months: readonly string[];
  readonly initialExpanded?: ExpandedState;
}

const columnHelper = createColumnHelper<CashflowTableRow>();

export const CashflowTable: FC<CashflowTableProps> = ({
  data,
  months,
  initialExpanded,
}) => {
  const { t } = useTranslation();

  const calculateRowTotal = useCallback(
    (rowData: CashflowTableRow): number => {
      return months.reduce((sum, month) => {
        const value = rowData[month] as number;
        return sum + (value || 0);
      }, 0);
    },
    [months],
  );

  const columns = useMemo(() => {
    const conceptColumn = columnHelper.accessor('concept', {
      header: t('common.concept', { ns: 'common' }),
      maxSize: 300,
      cell: ({ getValue, row }) => (
        <div className="flex items-center gap-x-2">
          <Table.Expander expander={row} />
          {row.getCanExpand() || row.original.parent ? (
            <span>
              <Translation
                i18nKey={`aggregator.cashflow.${row.original.type}` as any}
                ns="aggregator"
              />
            </span>
          ) : (
            <span>{getValue()}</span>
          )}
        </div>
      ),
    });

    const monthColumns = months.map(month =>
      columnHelper.accessor(month, {
        header: month,
        meta: { alignment: 'end' },
        cell: ({ getValue, row }) => {
          const value = getValue() as number;
          return (
            <div
              className={classNames({
                'text-primary-green': row.original.type === 'inflows',
                'text-primary-red': row.original.type === 'outflows',
              })}
            >
              <Table.Data
                value={value}
                format="currency"
                emptyValue={0}
                safeValue="-"
                currency={row.original.currency}
              />
            </div>
          );
        },
      }),
    );

    const totalColumn = columnHelper.display({
      id: 'total',
      header: t('common.total', { ns: 'common' }),
      meta: { alignment: 'end' },
      cell: ({ row }) => {
        const total = calculateRowTotal(row.original);
        return (
          <div
            className={classNames({
              'text-primary-green': row.original.type === 'inflows',
              'text-primary-red': row.original.type === 'outflows',
            })}
          >
            <Table.Data
              value={total}
              format="currency"
              emptyValue={0}
              safeValue="-"
              currency={row.original.currency}
            />
          </div>
        );
      },
    });

    return [conceptColumn, ...monthColumns, totalColumn];
  }, [months, t, calculateRowTotal]);

  return (
    <div className="overflow-x-auto">
      <Table.Grouped
        data={data}
        columns={columns}
        initialExpanded={initialExpanded}
        disableDeselect
        data-testid="cashflow-table"
      />
    </div>
  );
};
